import { FOOTER_PREMIUM } from "../common/footer";
import { PULSE_HEADER } from "../common/header";

/**
 * World-Class Usage Report Template
 * Features: Plan card, colorful usage meters, bot breakdown table, alerts, upgrade CTA
 */
export const usageReport = `
<mjml>
  <mj-head>
    <mj-title>Account Usage Report - {{report_period}}</mj-title>
    <mj-preview>Your account usage summary and resource consumption</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .usage-low { background: linear-gradient(90deg, #10B981, #34D399); }
      .usage-medium { background: linear-gradient(90deg, #F59E0B, #FBBF24); }
      .usage-high { background: linear-gradient(90deg, #EF4444, #F87171); }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${PULSE_HEADER("Usage Report", "{{report_period}}")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hello {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Here's your account usage report for {{product_name}}. This report shows how you're utilizing your plan resources and helps you stay on top of your quotas.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Current Plan Card -->
    <mj-section background-color="#1939B7" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="rgba(255,255,255,0.8)" text-transform="uppercase" letter-spacing="2px" align="center" padding-bottom="5px">
          Current Plan
        </mj-text>
        <mj-text font-size="36px" color="#ffffff" font-weight="800" align="center" padding="5px 0">
          {{current_plan}}
        </mj-text>
        <mj-text font-size="14px" color="rgba(255,255,255,0.7)" align="center">
          Billing cycle: {{billing_cycle_start}} - {{billing_cycle_end}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Resource Usage Header -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Quotas
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          Resource Usage
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Messages Usage -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%; margin-bottom: 10px;">
            <tr>
              <td>
                <span style="font-size: 20px; margin-right: 10px;">&#128172;</span>
                <strong style="font-size: 16px; color: #0f172a;">Messages</strong>
              </td>
              <td style="text-align: right;">
                <span style="font-size: 14px; color: #64748b;">{{messages_used}}</span>
                <span style="font-size: 14px; color: #94a3b8;"> / </span>
                <span style="font-size: 14px; font-weight: 600; color: #0f172a;">{{messages_limit}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 14px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, {{messages_color}}, {{messages_color}}); height: 100%; width: {{messages_percentage}}%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="{{messages_color}}" font-weight="600" padding-top="8px">
          {{messages_percentage}}% used
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Conversations Usage -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%; margin-bottom: 10px;">
            <tr>
              <td>
                <span style="font-size: 20px; margin-right: 10px;">&#128483;</span>
                <strong style="font-size: 16px; color: #0f172a;">Conversations</strong>
              </td>
              <td style="text-align: right;">
                <span style="font-size: 14px; color: #64748b;">{{conversations_used}}</span>
                <span style="font-size: 14px; color: #94a3b8;"> / </span>
                <span style="font-size: 14px; font-weight: 600; color: #0f172a;">{{conversations_limit}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 14px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, {{conversations_color}}, {{conversations_color}}); height: 100%; width: {{conversations_percentage}}%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="{{conversations_color}}" font-weight="600" padding-top="8px">
          {{conversations_percentage}}% used
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Chatbots Usage -->
    <mj-section background-color="#ffffff" padding="10px 30px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%; margin-bottom: 10px;">
            <tr>
              <td>
                <span style="font-size: 20px; margin-right: 10px;">&#129302;</span>
                <strong style="font-size: 16px; color: #0f172a;">Chatbots</strong>
              </td>
              <td style="text-align: right;">
                <span style="font-size: 14px; color: #64748b;">{{chatbots_used}}</span>
                <span style="font-size: 14px; color: #94a3b8;"> / </span>
                <span style="font-size: 14px; font-weight: 600; color: #0f172a;">{{chatbots_limit}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 14px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, {{chatbots_color}}, {{chatbots_color}}); height: 100%; width: {{chatbots_percentage}}%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="{{chatbots_color}}" font-weight="600" padding-top="8px">
          {{chatbots_percentage}}% used
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- AI Credits Usage -->
    <mj-section background-color="#ffffff" padding="10px 30px 30px" border-radius="0 0 16px 16px">
      <mj-column padding="20px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%; margin-bottom: 10px;">
            <tr>
              <td>
                <span style="font-size: 20px; margin-right: 10px;">&#129504;</span>
                <strong style="font-size: 16px; color: #0f172a;">AI Credits</strong>
              </td>
              <td style="text-align: right;">
                <span style="font-size: 14px; color: #64748b;">{{ai_credits_used}}</span>
                <span style="font-size: 14px; color: #94a3b8;"> / </span>
                <span style="font-size: 14px; font-weight: 600; color: #0f172a;">{{ai_credits_limit}}</span>
              </td>
            </tr>
          </table>
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 14px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, {{ai_credits_color}}, {{ai_credits_color}}); height: 100%; width: {{ai_credits_percentage}}%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="{{ai_credits_color}}" font-weight="600" padding-top="8px">
          {{ai_credits_percentage}}% used
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Usage by Chatbot -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Breakdown
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Usage by Chatbot
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b; text-transform: uppercase;">Chatbot</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Messages</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Chats</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">AI Credits</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #1939B7; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">1</span>
              {{bot_1_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #1939B7;">{{bot_1_messages}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_1_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #8B5CF6;">{{bot_1_credits}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #64748B; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">2</span>
              {{bot_2_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #1939B7;">{{bot_2_messages}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_2_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #8B5CF6;">{{bot_2_credits}}</td>
          </tr>
          <tr>
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #94A3B8; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">3</span>
              {{bot_3_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #1939B7;">{{bot_3_messages}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_3_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; color: #8B5CF6;">{{bot_3_credits}}</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Alert Section -->
    <mj-section background-color="#FEF3C7" padding="25px 30px" border-radius="16px" border-left="4px solid #F59E0B">
      <mj-column>
        <mj-text font-size="16px" color="#92400E" font-weight="700" padding-bottom="8px">
          &#9888; {{usage_alert_title}}
        </mj-text>
        <mj-text font-size="14px" color="#92400E">
          {{usage_alert_message}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Action Buttons -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column width="48%" padding="5px">
        <mj-button background-color="#1939B7" color="#ffffff" href="{{dashboard_url}}" border-radius="8px" font-weight="600" width="100%" font-size="15px">
          View Dashboard
        </mj-button>
      </mj-column>
      <mj-column width="48%" padding="5px">
        <mj-button background-color="#10B981" color="#ffffff" href="{{upgrade_url}}" border-radius="8px" font-weight="600" width="100%" font-size="15px">
          Upgrade Plan &#8593;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Need More Section -->
    <mj-section background-color="#f0f9ff" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="18px" color="#0f172a" font-weight="700" padding-bottom="10px">
          &#128640; Need More Resources?
        </mj-text>
        <mj-text font-size="14px" color="#64748b">
          Upgrade your plan to unlock more messages, conversations, and AI credits. Get the power you need to scale your chatbot operations.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "usageReport",
  name: "Usage Report - Premium",
  category: "pulse",
  purpose: "pulse" as const,
  description:
    "A world-class usage report with bold plan card, colorful usage meters with gradient progress bars, chatbot breakdown table, usage alerts, and upgrade CTAs.",
  features: [
    "Bold current plan card",
    "Colorful usage meters with icons",
    "Gradient progress bars",
    "Usage by chatbot breakdown",
    "Warning alerts styling",
    "Dual action buttons",
  ],
  subject: "&#128202; Account Usage Report - {{report_period}}",
  subTitle: "Your account usage summary and resource consumption",
  mjmlTemplate: usageReport,
  tags: ["pulse", "usage", "quota", "account", "report", "resources"],
};
